import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import config from '../config';
import { toast } from 'react-toastify';
import { IonIcon } from '@ionic/react';
import { chatbubbleOutline, sendOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { 
  IonButton, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonTextarea, 
  IonPage,
  IonRouterOutlet,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route } from 'react-router-dom';
import './AIChat.css';
import OpenAI from 'openai';
import ReactMarkdown from 'react-markdown';

// Helper function to check if a value is not null/blank
const isValueNonEmpty = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (typeof value === 'object' && Object.keys(value).length === 0) return false;
  return true;
};

// Helper function to extract loaded sections from JSON
const extractLoadedSections = (data) => {
  if (!data || typeof data !== 'object') return [];
  
  const sections = [];
  for (const key in data) {
    if (isValueNonEmpty(data[key])) {
      sections.push(key);
    }
  }
  return sections;
};

const AIChat = ({ member_id, user_id, auth_token }) => {
  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  
  // Setup panel state
  const [systemPrompt, setSystemPrompt] = useState('You are a clinical chatbot tasked with answering patient questions about their health data. Maintain a friendly, professional tone and use concise sentences in plain language when possible. Communicate clear, factual information; avoid emotional reassurance. Safeguard patient privacy and never fabricate or guess health data. If the patient describes an emergency or red-flag symptom, advise them to contact emergency services immediately before continuing. If external data is needed, prefer current clinical guidelines and reputable authorities, and state limits or uncertainties clearly. Use South African English. Format your responses in markdown format.');
  // removed this from systemPrompt - When you cite external medical sources, include references (APA format acceptable). 
  const [developerPrompt, setDeveloperPrompt] = useState('Rely exclusively on the provided patient data and established, evidence-based medical knowledge. Do not speculate or hypothesize. Provide advice or recommendations only if the patient explicitly requests them. If information is missing or uncertainty remains, say so rather than guessing. Never invent lab values, vital signs, or diagnoses. Use internal reasoning as needed but do not include step-by-step notes or checklists in the output. When helpful, surface only observable checks (e.g., "Med-med interactions reviewed; allergy conflicts reviewed") - no chain-of-thought.');
  const [firstQuestion, setFirstQuestion] = useState('Please review my medications and let me know if there are any potential interactions between them, or if any may cause concerns given my allergies.');
  const [selectedModel, setSelectedModel] = useState('gpt-5-2025-08-07');
  const [defaultPrompt, setDefaultPrompt] = useState('Medicine Evaluation');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [jsonContent, setJsonContent] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLoadingJson, setIsLoadingJson] = useState(false);
  const [jsonLoadError, setJsonLoadError] = useState(null);
  const [loadedSections, setLoadedSections] = useState([]);
  const [hasLoadedData, setHasLoadedData] = useState(false); // Track if we've successfully loaded data in this session
  
  // Track effective auth data (from props or postMessage)
  const [effectiveMemberId, setEffectiveMemberId] = useState(member_id || localStorage.getItem('member_id') || null);
  const [effectiveAuthToken, setEffectiveAuthToken] = useState(auth_token || localStorage.getItem('auth_token') || null);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasFetchedJsonRef = useRef(false);

  // Initialize OpenAI
  const openai = useMemo(() => new OpenAI({
    apiKey: config.openaiApiKey,
    dangerouslyAllowBrowser: true
  }), []);

  // Helper function to reset chat state
  const resetChat = () => {
    setMessages([]);
    setInputMessage('');
    setChatStarted(false);
    setJsonContent(null);
    setLoadedSections([]);
    setHasLoadedData(false);
    setJsonLoadError(null);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Define allowed origins for postMessage
  const allowedOrigins = useMemo(() => [
    'https://www.infinity-mc-hms.com',
    'https://infinity-mc-healthplan-edit-dot-infinity-medical-concier-724b0.lm.r.appspot.com',
    'http://localhost:3000',
    'http://localhost:5000',
    'https://localhost:3000',
    'https://localhost:5000'
  ], []);

  // Add message event listener to receive auth data
  useEffect(() => {
    console.log('Setting up message listener...');
    console.log('Parent window exists:', !!window.parent);
    console.log('Is same origin:', window.parent === window);
    
    const handleMessage = (event) => {
      console.log('Received message:', {
        ...event,
        data: event.data?.target ? { target: event.data.target } : event.data
      });
      
      // Ignore MetaMask and webpack messages
      if (event.data?.target === 'metamask-contentscript' || 
          event.data?.target === 'metamask-inpage' ||
          event.data?.type === 'webpackWarnings') {
        return;
      }

      console.log('Processing message from origin:', event.origin);
      
      // Verify the origin of the message
      if (!allowedOrigins.includes(event.origin)) {
        console.log('Message origin not in allowed list:', event.origin);
        console.log('Allowed origins:', allowedOrigins);
        return;
      }

      // Verify the message structure
      if (event.data && event.data.type === 'AUTH_DATA') {
        const { member_id: incomingMemberId, user_id: incomingUserId, auth_token: incomingAuthToken } = event.data;
        console.log('Auth data received:', { 
          member_id: incomingMemberId, 
          user_id: incomingUserId, 
          auth_token: incomingAuthToken ? '***' : undefined
        });
        
        if (incomingMemberId && incomingUserId && incomingAuthToken) {
          console.log('Setting auth data...');
          
          // Check if this is a different member/user (re-initialization)
          const currentMemberId = effectiveMemberId || localStorage.getItem('member_id');
          const isNewMember = currentMemberId !== incomingMemberId;
          
          // Store auth data in localStorage for API calls
          localStorage.setItem('member_id', incomingMemberId);
          localStorage.setItem('user_id', incomingUserId);
          localStorage.setItem('auth_token', incomingAuthToken);
          
          // If this is a new member/user, reset the chat
          if (isNewMember) {
            console.log('New member detected, resetting chat...');
            resetChat();
          }
          
          // Update state to trigger fetch
          setEffectiveMemberId(incomingMemberId);
          setEffectiveAuthToken(incomingAuthToken);
          
          // Reset fetch ref so we can fetch with new auth data
          hasFetchedJsonRef.current = false;
          
          console.log('Auth data successfully set');
        } else {
          console.error('Missing required auth data:', { 
            hasMemberId: !!incomingMemberId, 
            hasUserId: !!incomingUserId, 
            hasAuthToken: !!incomingAuthToken 
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request auth data from parent only if we don't have it
    if (!member_id || !user_id || !auth_token) {
      console.log('Requesting auth data from parent...');
      allowedOrigins.forEach(origin => {
        try {
          window.parent.postMessage({ type: 'REQUEST_AUTH' }, origin);
          console.log('Auth request sent to origin:', origin);
        } catch (error) {
          console.error('Error sending auth request to origin:', origin, error);
        }
      });
    } else {
      console.log('Already have auth data, skipping request');
    }

    return () => {
      console.log('Cleaning up message listener...');
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize component
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      // Check if this is a different member/user (re-initialization)
      const currentMemberId = effectiveMemberId || localStorage.getItem('member_id');
      const isNewMember = currentMemberId !== member_id;
      
      // Store auth data in localStorage for API calls
      localStorage.setItem('member_id', member_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('auth_token', auth_token);
      
      // If this is a new member/user, reset the chat
      if (isNewMember) {
        console.log('New member detected via props, resetting chat...');
        resetChat();
      }
      
      // Update state to trigger fetch
      setEffectiveMemberId(member_id);
      setEffectiveAuthToken(auth_token);
      
      // Reset fetch ref so we can fetch with new auth data
      hasFetchedJsonRef.current = false;
      
      // Initialize chat with welcome message
      /*setMessages([
        {
          id: 1,
          type: 'ai',
          content: 'Hello! I\'m your AI health assistant. How can I help you today?',
          timestamp: new Date().toISOString()
        }
      ]);*/
    }
  }, [member_id, user_id, auth_token, effectiveMemberId]);

  // Fetch JSON data from endpoint on initialization
  useEffect(() => {
    const fetchJsonData = async () => {
      // Only fetch if we have both member_id and auth_token, and haven't fetched yet
      if (effectiveMemberId && effectiveAuthToken && !hasFetchedJsonRef.current) {
        hasFetchedJsonRef.current = true;
        setIsLoadingJson(true);
        setJsonLoadError(null);
        
        console.log('Fetching JSON data with member_id:', effectiveMemberId);
        
        try {
          const response = await fetch('https://feedback-app-367524407367.europe-west2.run.app/digital_twin', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              authtoken: effectiveAuthToken,
              member_id: parseInt(effectiveMemberId) || effectiveMemberId
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          setJsonContent(data);
          
          // Extract loaded sections
          const sections = extractLoadedSections(data);
          setLoadedSections(sections);
          
          // Mark that we've successfully loaded data
          setHasLoadedData(true);
          
          // Show success message with sections
          if (sections.length > 0) {
            const sectionsList = sections.join(', ');
            toast.success(`Medical data loaded successfully - loaded ${sectionsList}`);
          } else {
            toast.success('Medical data loaded successfully');
          }
        } catch (error) {
          console.error('Error fetching JSON data:', error);
          setJsonLoadError(error.message);
          toast.error(`Failed to load medical data: ${error.message}`);
          // Reset ref on error so user can retry if needed
          hasFetchedJsonRef.current = false;
        } finally {
          setIsLoadingJson(false);
        }
      }
    };

    fetchJsonData();
  }, [effectiveMemberId, effectiveAuthToken]);

  // Handle sending messages
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Prepare messages for OpenAI (include system and developer prompts)
      const openaiMessages = [
        { role: "system", content: systemPrompt },
        { role: "developer", content: developerPrompt },
        { role: "developer", content: JSON.stringify(jsonContent) },
        ...messages.map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        })),
        { role: "user", content: userMessage.content }
      ];

      // Call OpenAI API
      const response = await openai.chat.completions.create({
        //reasoning: { effort: "low" }, // "low" | "medium" | "high"
        model: selectedModel,
        messages: openaiMessages
      });

      const aiResponse = response.choices[0].message?.content || "I'm sorry, I couldn't process your request.";

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, systemPrompt, developerPrompt, jsonContent, messages, selectedModel, openai]);

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file upload (currently hidden)
  // eslint-disable-next-line no-unused-vars
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setUploadedFile(file);
      
      // Read JSON file content
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = JSON.parse(e.target.result);
          setJsonContent(content);
          
          // Extract loaded sections
          const sections = extractLoadedSections(content);
          setLoadedSections(sections);
          
          // Show success message with sections
          if (sections.length > 0) {
            const sectionsList = sections.join(', ');
            toast.success(`File "${file.name}" uploaded - loaded ${sectionsList}`);
          } else {
            toast.success(`File "${file.name}" uploaded and parsed successfully`);
          }
        } catch (error) {
          toast.error('Invalid JSON file format');
          setUploadedFile(null);
          setJsonContent(null);
          setLoadedSections([]);
        }
      };
      reader.readAsText(file);
    } else {
      toast.error('Please select a valid JSON file');
    }
  };

  // Define prompt templates
  const promptTemplates = {
    'Medicine Evaluation': 'Please review my medications and let me know if there are any potential interactions between them, or if any may cause concerns given my allergies.',
    'Relevant Recent News Articles': 'Based on the patients medical history, find between 2 and 5 recent news articles that are likely to be of interest and relevant to this individual. Only include articles from science-based, evidence-driven, and medically trusted sources such as: Peer-reviewed journals (e.g., JAMA, NEJM, The Lancet, BMJ), Major medical institutions (e.g., Mayo Clinic, Cleveland Clinic, Johns Hopkins Medicine), Government health agencies (e.g., NIH, CDC, FDA, WHO, NHS), Trusted medical news outlets (e.g., MedPage Today, Medscape, STAT News, Healthline when citing studies). Exclude general news outlets, blogs, opinion pieces, or commercial/non-scientific sources. For each article, provide: 1. The title 2. A one-sentence summary tailored to the patients medical background 3. The URL. Ensure the articles are recent (within the last 12–18 months unless highly relevant), evidence-based, and free of misinformation. Dont offer any follow-up suggestions.',
    'Medical Analysis': 'Analyze the attached medical data using the following structured approach: Summarize patient history (past medical history, past surgical history, allergies, medications). Provide a structured assessment of the above data, referencing relevant current American (AHA/ACC, ADA, etc.) and European (ESC, EAS, etc.) guidelines, integrating insights from OpenEvidence AI, and avoiding untested or alternative therapies. Identify any allergies of concern and check for potential drug–drug interactions. Summarize key medical concerns, suggest further diagnostic tests and management options, outline follow-up consultations with appropriate specialists, and generate a draft lab request document.',
    'Basic Summary': 'Create a basic summary of the medical evaluation. Only include the assessment and follow-up recommendations. Present the information clearly, while adding gentle humour to make it engaging but still respectful of the medical context.',
    'Predictive Tools': 'Use the provided medical data to calculate the PREVENT score. Then repeat the PREVENT score calculations with the following changes: (1) Weight loss of 10%. (2) LDL reduction by 10%. (3) Blood pressure reduction by 10%. (4) UACR reduction by 10%. (5) Combination of 10% weight loss and 10% LDL reduction. (6) Aggressive multifactorial risk reduction. Present each scenario clearly and comparatively.',
    'Activity analysis': 'Use the provided wearable data (where available) to create a 10-week plan to achieve the target of [x]. Adapt the plan based on the patient\'s baseline activity levels, ensuring it is realistic and progressive. Account for any religious or cultural considerations provided (e.g., avoiding exercise on the Sabbath or Jewish holidays). Present the plan in a clear, week-by-week format with actionable steps.',
    'Health Plan Analysis': `You are a clinical decision-support assistant specializing in preventive and integrative medicine. You will receive a patient background as structured JSON (e.g., memberInfo, assessmentInfo, medicalHistory, doctors, currentMedicalConcerns, mediumTermPlans, encounters, vaccinationSchedule, laboratoryResults/pathology_results, wearableData).

Task:
Identify additional follow-up recommendations that are not already addressed in the existing plan.

Output:
Return the following fields for each suggestion:
	•	"Subject"
	•	"Description"
	•	"Service Provider"
	•	"Due Date"
 Do not include administrative/general items such as "Advance Care Planning" unless explicitly requested.

Clinical guardrails and data-usage rules:
	1.	Demographics and preferences:
	•	Read sex from memberInfo.sex and age from memberInfo.age. Never apply female-only guidance to male patients. If any suggestion includes sex-incongruent language (e.g., "postmenopausal female" for a male), discard it.
	•	Respect scheduling or lifestyle constraints in memberInfo.additional_AI_instructions (e.g., Sabbath/holiday exercise restrictions).
	2.	Deduplication against completed/scheduled care:
	•	Parse encounters and mediumTermPlans. Do NOT re-suggest services already completed within the recommended interval or already scheduled. For colorectal cancer screening, recommend ONLY if the most recent colonoscopy is beyond the guideline interval OR if prior pathology dictates earlier surveillance; otherwise exclude it.
	•	Also deduplicate against tasks in medicalConcerns.tasks and anything in vaccinationSchedule.
	3.	Labs/pathology and diagnostic certainty:
	•	Use the most recent lab values by date. Perform correct unit conversion where needed (e.g., LDL: 1 mmol/L = 38.67 mg/dL) before comparing to thresholds.
	•	Apply KDIGO chronicity (≥3 months) for CKD staging and albuminuria; if abnormalities are not persistent, suggest routine monitoring rather than specialist referral.
	•	For cytopenias (e.g., thrombocytopenia), use persistence and thresholds (e.g., platelet <150 ×10^9/L persistent; consider hematology referral if <100 or symptomatic) and reference dates/values in the Description.
	•	Do not infer diabetes without a documented diagnosis or diagnostic labs (e.g., HbA1c ≥6.5% confirmed). If criteria are not met, exclude diabetes-specific surveillance.
	4.	Oncology and therapy-specific logic:
	•	If prostate cancer and androgen deprivation therapy (e.g., leuprolide, enzalutamide) are present, you may recommend baseline and periodic DEXA for a MALE ONLY when the indication is ADT; explicitly state "indication: ADT" in the Description. Include ADT surveillance items (e.g., PSA and testosterone cadence) only if not already in the plan.
	5.	Sleep and wearable data:
	•	If "sleep apnoea" is diagnosed or wearableData shows recurrent desaturations/poor sleep quality, prefer actionable follow-ups (e.g., CPAP adherence download, mask fit, re-titration) over generic OSA screening. Reference the relevant wearable metric/date in the Description.
	6.	Immunizations:
	•	Recommend vaccines only if missing or due per CDC adult schedule, deduplicating against vaccinationSchedule and encounters. For Hepatitis B in adults ≥60, suggest only when risk factors are evident or documentation is absent and risk is plausible. For influenza/RSV, align timing to the patient's locale (assume Africa/Johannesburg seasons).
	7.	ServiceProvider selection:
	•	Prefer named clinicians from the doctors array whose specialty matches the recommendation; otherwise specify the specialty (e.g., "Nephrology", "Radiology—Bone Density").
	8.	Make every item patient-specific and evidence-linked:
	•	In Description, briefly cite the exact trigger(s) from this patient's data (e.g., "Platelets 121 and 127 ×10^9/L on 2025-04-30 and 2025-05-16 (persistent)"; "Ferritin 399 → 321 µg/L trend"; "SpO₂ min 87–91% on multiple nights in Oct 2025").
	9.	DueDate construction:
	•	Use clear timeframes (e.g., "Within 1 month", "Every 3–6 months", "Annually"). For seasonal vaccines, specify the next relevant season for South Africa. Avoid absolute dates unless a guideline-driven interval from a known last-done date is required.
	10.	Final quality checks before output:
	•	Remove any item that duplicates an existing plan/schedule or conflicts with the latest labs, encounters, or diagnoses.
	•	Ensure all language is sex-appropriate and guideline-appropriate for this patient.`
  };

  // Handle default prompt selection
  const handleDefaultPromptChange = (selectedPrompt) => {
    setDefaultPrompt(selectedPrompt);
    
    if (promptTemplates[selectedPrompt]) {
      setFirstQuestion(promptTemplates[selectedPrompt]);
    }
  };

  // Handle start chat
  const handleStartChat = async () => {
    if (!systemPrompt.trim()) {
      toast.error('Please provide a system prompt');
      return;
    }
    
    if (!jsonContent) {
      toast.error('Please upload a JSON file');
      return;
    }
    
    setChatStarted(true);
    setIsLoading(true);
    
    try {
      // Prepare messages for OpenAI
      const messages = [
        { role: "system", content: systemPrompt },
        { role: "developer", content: developerPrompt },
        { role: "developer", content: JSON.stringify(jsonContent) },
        { role: "user", content: firstQuestion }
      ];

      // Call OpenAI API
      const response = await openai.chat.completions.create({
        //reasoning: { effort: "low" }, // "low" | "medium" | "high"
        model: selectedModel,
        messages: messages
      });

      const aiResponse = response.choices[0].message?.content || "I'm sorry, I couldn't process your request.";

      // Set up chat with initial messages
      setMessages([
        {
          id: 1,
          type: 'user',
          content: firstQuestion,
          timestamp: new Date().toISOString()
        },
        {
          id: 2,
          type: 'ai',
          content: aiResponse,
          timestamp: new Date().toISOString()
        }
      ]);
      
      toast.success('Chat started successfully!');
      
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Failed to start chat. Please try again.');
      setChatStarted(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle new chat (restart conversation)
  const handleNewChat = async () => {
    if (!systemPrompt.trim()) {
      toast.error('Please provide a system prompt');
      return;
    }
    
    if (!jsonContent) {
      toast.error('Please upload a JSON file');
      return;
    }
    
    // Clear existing messages and input first
    setMessages([]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      // Prepare messages for OpenAI
      const messages = [
        { role: "system", content: systemPrompt },
        { role: "developer", content: developerPrompt },
        { role: "developer", content: JSON.stringify(jsonContent) },
        { role: "user", content: firstQuestion }
      ];

      // Call OpenAI API
      const response = await openai.chat.completions.create({
        //reasoning: { effort: "low" }, // "low" | "medium" | "high"
        model: selectedModel,
        messages: messages
      });

      const aiResponse = response.choices[0].message?.content || "I'm sorry, I couldn't process your request.";

      // Set new initial messages
      setMessages([
        {
          id: 1,
          type: 'user',
          content: firstQuestion,
          timestamp: new Date().toISOString()
        },
        {
          id: 2,
          type: 'ai',
          content: aiResponse,
          timestamp: new Date().toISOString()
        }
      ]);
      
      toast.success('New chat started!');
      
    } catch (error) {
      console.error('Error starting new chat:', error);
      toast.error('Failed to start new chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <IonPage> 

      <IonContent className="ai-chat-content">
        <div className="ai-chat-container">
          {/* Two Column Layout */}
          <div className="ai-chat-layout">
            {/* Left Column - Setup Panel */}
            <div className="setup-panel">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>AI Chat Setup</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {/* Medical Data Status */}
                  <IonItem>
                    <IonLabel position="stacked">Medical Data</IonLabel>
                    {isLoadingJson && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ion-color-primary)' }}>
                        ⏳ Loading medical data from server...
                      </div>
                    )}
                    {hasLoadedData && jsonContent && !uploadedFile && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ion-color-success)' }}>
                        ✓ Medical data loaded from server{loadedSections.length > 0 && ` - loaded ${loadedSections.join(', ')}`}
                      </div>
                    )}
                    {jsonLoadError && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ion-color-danger)' }}>
                        ⚠ Failed to load: {jsonLoadError}
                      </div>
                    )}
                    {uploadedFile && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ion-color-success)' }}>
                        ✓ {uploadedFile.name} (overrides server data)
                      </div>
                    )}
                    {!hasLoadedData && !jsonContent && !isLoadingJson && !jsonLoadError && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                        Medical data will be loaded automatically from server
                      </div>
                    )}
                  </IonItem>

                  {/* File Upload (Fallback) - Hidden for now */}
                  {/* <IonItem>
                    <IonLabel position="stacked">Upload JSON File (Optional)</IonLabel>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      style={{ marginTop: '8px' }}
                    />
                  </IonItem> */}

                  {/* Advanced Settings Toggle */}
                  <IonButton
                    fill="clear"
                    expand="block"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ 
                      marginTop: '16px',
                      marginBottom: '8px',
                      '--color': '#666',
                      fontSize: '14px'
                    }}
                  >
                    <IonIcon 
                      icon={showAdvanced ? chevronUpOutline : chevronDownOutline} 
                      style={{ marginRight: '8px' }}
                    />
                    Advanced Settings
                  </IonButton>

                  {/* Advanced Settings (Collapsible) */}
                  {showAdvanced && (
                    <div style={{ marginBottom: '16px' }}>
                      {/* System Prompt */}
                      <IonItem>
                        <IonLabel position="stacked">System Prompt</IonLabel>
                        <IonTextarea
                          value={systemPrompt}
                          onIonInput={(e) => setSystemPrompt(e.detail.value)}
                          placeholder="Enter system prompt..."
                          rows={5}
                          style={{ marginTop: '8px' }}
                        />
                      </IonItem>

                      {/* Developer Prompt */}
                      <IonItem>
                        <IonLabel position="stacked">Developer Prompt</IonLabel>
                        <IonTextarea
                          value={developerPrompt}
                          onIonInput={(e) => setDeveloperPrompt(e.detail.value)}
                          placeholder="Enter developer prompt..."
                          rows={5}
                          style={{ marginTop: '8px' }}
                        />
                      </IonItem>

                      {/* Model Selection */}
                      <IonItem>
                        <IonLabel position="stacked">Model</IonLabel>
                        <IonSelect
                          value={selectedModel}
                          onIonChange={(e) => setSelectedModel(e.detail.value)}
                          style={{ marginTop: '8px' }}
                        >
                          <IonSelectOption value="gpt-5-2025-08-07">GPT-5</IonSelectOption>
                          {/* <IonSelectOption value="o4-mini-deep-research-2025-06-26">o4-mini-deep-research</IonSelectOption> */}
                        </IonSelect>
                      </IonItem>
                    </div>
                  )}

                  {/* Default Prompt Selection */}
                  <IonItem>
                    <IonLabel position="stacked">Default Prompts</IonLabel>
                    <IonSelect
                      value={defaultPrompt}
                      onIonChange={(e) => handleDefaultPromptChange(e.detail.value)}
                      placeholder="Select a default prompt..."
                      style={{ marginTop: '8px' }}
                    >
                      {Object.keys(promptTemplates).map((key) => (
                        <IonSelectOption key={key} value={key}>{key}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>

                  {/* First Question */}
                  <IonItem>
                    <IonLabel position="stacked">First Question</IonLabel>
                    <IonTextarea
                      value={firstQuestion}
                      onIonInput={(e) => setFirstQuestion(e.detail.value)}
                      placeholder="Enter the first question to ask..."
                      rows={4}
                      style={{ marginTop: '8px' }}
                    />
                  </IonItem>

                  {/* Start Chat / New Chat Button */}
                  <IonButton
                    expand="block"
                    onClick={chatStarted ? handleNewChat : handleStartChat}
                    disabled={isLoading || !jsonContent || !hasLoadedData}
                    style={{ marginTop: '16px' }}
                  >
                    {isLoading ? 'Loading...' : (chatStarted ? 'New Chat' : 'Start Chat')}
                  </IonButton>
                </IonCardContent>
              </IonCard>
            </div>

            {/* Right Column - Chat Interface */}
            <div className="chat-panel">
              {!chatStarted ? (
                <div className="chat-placeholder">
                  <IonIcon icon={chatbubbleOutline} size="large" />
                  <h3>AI Healthcare Assistant</h3>
                  <p>Click "Start Chat" to begin your AI conversation</p>
                </div>
              ) : (
                <>
                  {/* Messages Area */}
                  <div className="messages-container">
                    {messages.map((message) => (
                      <div key={message.id} className={`message ${message.type}`}>
                        <div className="message-content">
                          <div className="message-text">
                            {message.type === 'ai' ? (
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            ) : (
                              message.content
                            )}
                          </div>
                          <div className="message-time">{formatTime(message.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                    
                    {isLoading && (
                      <div className="message ai">
                        <div className="message-content">
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area */}
                  <div className="input-container">
                    <IonItem className="input-item">
                      <IonTextarea
                        ref={inputRef}
                        value={inputMessage}
                        onIonInput={(e) => setInputMessage(e.detail.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message here..."
                        rows={1}
                        autoGrow={true}
                        maxlength={1000}
                        disabled={isLoading}
                      />
                      <IonButton
                        slot="end"
                        fill="clear"
                        onClick={handleSendMessage}
                        disabled={!inputMessage.trim() || isLoading}
                        className="send-button"
                      >
                        <IonIcon icon={sendOutline} />
                      </IonButton>
                    </IonItem>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

// Main component with routing
const AIChatWithRouter = () => {
  return (
    <IonReactRouter>
      <IonRouterOutlet>
        <Route path="/ai-chat" exact render={() => <AIChat />} />
      </IonRouterOutlet>
    </IonReactRouter>
  );
};

// Export both versions
export { AIChat };
export default AIChatWithRouter;
